import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { wizardInvoke } from "@/lib/publicWizard";

export type WizardKey = "perfume" | "corporate_gifting" | "perfume_gift";
export type TextInputMode = "none" | "optional" | "required";
export { SELF_OCCASION_QUESTION_ID } from "@/lib/selfWizardQuestions";
export interface WizardOption { id: string; question_id: string; label: string; label_ar: string | null; description?: string | null; description_ar?: string | null; display_order: number; is_active: boolean; is_exclusive?: boolean; canonical_value?: string | null; option_group?: string | null; option_group_ar?: string | null; text_input?: TextInputMode; occasion_filter?: string[] | null; default_first_for?: string[] | null; default_second_for?: string[] | null; }
export type WizardInputType = "select" | "reference_perfume" | "multi_select" | "text" | "values_select" | "wish_pair";
export interface WizardQuestion { id: string; section_id: string; question: string; question_ar: string | null; help_text: string | null; help_text_ar: string | null; input_type: WizardInputType; placeholder: string | null; placeholder_ar: string | null; display_order: number; is_active: boolean; is_required: boolean; semantic_role?: string | null; min_selections?: number | null; max_selections?: number | null; show_for_occasions?: string[] | null; options: WizardOption[]; }
export interface WizardSection { id: string; title: string; title_ar: string | null; description: string | null; description_ar: string | null; display_order: number; is_active: boolean; wizard_key?: WizardKey; questions: WizardQuestion[]; }

const db = supabase as any;

/** Public customer quizzes read ACTIVE configuration through a read-only function (tables stay staff-only). */
async function loadPublicConfig(wizardKey: WizardKey) {
  const { data, error } = await wizardInvoke("wizard-public-config", { action: "config", wizard_key: wizardKey }, "public");
  if (error || !data || data.error) return { s: [], q: [], o: [] };
  return { s: data.sections || [], q: data.questions || [], o: data.options || [] };
}

export const useWizardConfig = (activeOnly: boolean, wizardKey: WizardKey = "perfume", publicAccess = false) => {
  const [sections, setSections] = useState<WizardSection[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    let raw: { s: any[]; q: any[]; o: any[] };
    if (publicAccess) raw = await loadPublicConfig(wizardKey);
    else {
      const [s, q, o] = await Promise.all([
        db.from("wizard_sections").select("*").eq("wizard_key", wizardKey).order("display_order"),
        db.from("wizard_questions").select("*").eq("wizard_key", wizardKey).order("display_order"),
        db.from("wizard_options").select("*").order("display_order"),
      ]);
      raw = { s: s.data || [], q: q.data || [], o: o.data || [] };
    }
    const active = activeOnly || publicAccess;
    const opts: WizardOption[] = raw.o.filter((x: WizardOption) => !active || x.is_active);
    const qs: WizardQuestion[] = raw.q
      .filter((x: WizardQuestion) => !active || x.is_active)
      .map((x: WizardQuestion) => ({ ...x, options: opts.filter((op) => op.question_id === x.id) }));
    const ss: WizardSection[] = raw.s
      .filter((x: WizardSection) => !active || x.is_active)
      .map((x: WizardSection) => ({ ...x, questions: qs.filter((qq) => qq.section_id === x.id) }))
      .filter((x: WizardSection) => !active || x.questions.length > 0);
    setSections(ss);
    setLoading(false);
  }, [activeOnly, wizardKey, publicAccess]);

  useEffect(() => { reload(); }, [reload]);
  return { sections, loading, reload };
};

export const useIsSalesmanOrAdmin = (userId?: string) => {
  const [allowed, setAllowed] = useState(false);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    if (!userId) { setAllowed(false); setChecking(false); return; }
    setChecking(true);
    supabase.from("user_roles").select("role").eq("user_id", userId).then(({ data }) => {
      setAllowed(!!data?.some((r: any) => r.role === "admin" || r.role === "salesman"));
      setChecking(false);
    });
  }, [userId]);
  return { allowed, checking };
};
